import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../state/authStore.ts';
import { trackEvent } from '../analytics.ts';

/**
 * Persistent bottom-bar CTA shown on interest-signal pages (FAQ, sample report, vs/*,
 * roi-calculator). Appears after the user scrolls past 600 px so it doesn't compete with
 * the hero CTA on first paint.
 *
 * Ethical guardrails (EU UCPD / DSA compliance):
 *   - NOT shown to authenticated users (no nag on people who already engaged)
 *   - NOT shown on the landing page (hero CTA is the primary; would be duplicative)
 *   - NOT shown on auth-gated routes (sign-in, profile, billing) where it's confusing
 *   - NOT shown until the consent banner has been dismissed (avoid bottom-stack collision)
 *   - Real risk reversal copy ("7-day money-back") — no fake urgency, no fake scarcity
 *   - Dismissible by the user; dismissal persists in sessionStorage so we don't nag in a loop
 */

const DISMISS_KEY = 'eaa-sticky-cta-dismissed';
const SHOW_AT_SCROLL_PX = 600;

const SHOW_ON_PATHS = new Set<string>([
  '/sample',
  '/faq',
  '/roi-calculator',
  '/vs/accessibe',
  '/vs/userway',
  '/vs/siteimprove',
  '/about',
  '/changelog',
]);

function isConsentPending(): boolean {
  const match = document.cookie.split('; ').find((c) => c.startsWith('eaa-consent='));
  return !match;
}

export default function StickyCta() {
  const { pathname } = useLocation();
  const user = useAuthStore((s) => s.user);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState<boolean>(
    () => sessionStorage.getItem(DISMISS_KEY) === '1'
  );

  // Decide whether this route is eligible for the CTA at all.
  const eligible = !user && !dismissed && SHOW_ON_PATHS.has(pathname);

  useEffect(() => {
    if (!eligible) {
      setVisible(false);
      return;
    }

    // Don't reveal until the user has scrolled past the hero AND the consent banner is gone.
    function check() {
      const scrolled = window.scrollY >= SHOW_AT_SCROLL_PX;
      const consentResolved = !isConsentPending();
      setVisible(scrolled && consentResolved);
    }

    check();
    window.addEventListener('scroll', check, { passive: true });
    // The consent banner can be dismissed at any moment — poll once a second for a few seconds
    // so the bar reappears after the user accepts/rejects. Cheap, bounded.
    const interval = setInterval(check, 1_000);
    return () => {
      window.removeEventListener('scroll', check);
      clearInterval(interval);
    };
  }, [eligible]);

  function handleDismiss() {
    setDismissed(true);
    sessionStorage.setItem(DISMISS_KEY, '1');
    trackEvent('sticky_cta_dismissed', { path: pathname });
  }

  function handleClick() {
    trackEvent('sticky_cta_click', { path: pathname });
  }

  if (!visible) return null;

  return (
    <div className="sticky-cta" role="complementary" aria-label="Run your scan">
      <div className="sticky-cta-text">
        <strong>You're 60 seconds from a WCAG audit report.</strong>
        <span className="sticky-cta-sub">€29 single scan · 7-day money-back · EU VAT auto-applied</span>
      </div>
      <div className="sticky-cta-actions">
        <Link to="/sign-in" className="sticky-cta-btn" onClick={handleClick}>
          Run my scan
        </Link>
        <button
          type="button"
          className="sticky-cta-close"
          onClick={handleDismiss}
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}
